/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { Document } from 'docx';
import { DownloadMultiScope } from '../../types/download';
import { SurveyInitiative } from '@g17eco/types/survey';
import { DataPeriods } from '@utils/dataPeriods';
import { formatDate } from '@utils/date';
import { getStartDate } from '@utils/surveyData';
import { convertImgToBase64URL, paragraphArray } from './document-structure';
import { SimpleReportGenerator } from './simple-report-generator';
import { ReportGeneratorFn } from './types';
import { standards } from '@g17eco/core';
import { getImageBase64FromSVGBase64 } from '../../utils/image';

export const SASBReportGenerator: ReportGeneratorFn<Document> = async (
  survey: SurveyInitiative,
  reportData,
  downloadScope: DownloadMultiScope
): Promise<Document> => {
  // get base64URL of svg image
  const sasbSVGBase64 = await convertImgToBase64URL(standards.sasb.src);

  // convert it to supported docx image base64URL (ex: .png)
  const sasbPNGBase64 = await getImageBase64FromSVGBase64({ imgBase64: sasbSVGBase64, removeBase64Prefix: true });

  const generator = await SimpleReportGenerator({
    survey,
    reportData,
    reportType: 'sasb',
    reportLogo: sasbPNGBase64,
    color: 'B73238',
    downloadScope,
  });

  const reportingPeriod = survey.period ?? DataPeriods.Yearly;
  const reportingStartDate = formatDate(getStartDate(survey.effectiveDate, reportingPeriod), 'MMMM YYYY');
  const reportingEndDate = formatDate(survey.effectiveDate, 'MMMM YYYY');

  const companyName = survey.initiative.name;
  const reportingPeriodStr = reportingPeriod === DataPeriods.Monthly ? reportingEndDate : `${reportingStartDate} to ${reportingEndDate}`;

  const intro = paragraphArray([
    'For readers that want to compare our sustainability performance against our peers, we include this appendix. ' +
    'The Sustainability Accounting Standards Board (SASB) is an independent standards-setting organization that supports ' +
    'reporting standards for companies to identify, manage and communicate sustainability information to their stakeholders. ' +
    'This 2021 SASB Report is our first disclosure pursuant to the SASB standards, and as such, our disclosures on these ' +
    'topics will evolve and mature with time.',

    '',

    'SASB guidelines differ depending on the industry. For more information on SASB, visit www.sasb.org. All data covers ' +
    `the period of ${reportingPeriodStr}. It includes all of ${companyName} wholly owned entities.`,

    '',

    'Where noted, in accordance with SASB Standards Application Guidance section 2.2, certain information is not ' +
    'included in this appendix, to the extent the information is not considered material; is privileged or confidential; '+
    'or is not currently collected in a manner wholly correlative with the related SASB metric. Where possible we have '+
    'shared alternative information that we believe may be insightful.'
  ])

  return generator.generate({
    getStyles: generator.getStyles,
    getLogos: generator.getLogos,
    getHeading: generator.getHeading,
    getSubHeading: () => [intro],
    getTables: generator.getTables,
    getAppendix: generator.getAppendix,
  });
}

